import React, { useContext, useEffect, useMemo, memo, useRef, forwardRef, useCallback, useImperativeHandle } from 'react';
import { useOverlayToggle, consoleLogDev, defaultQueryFn, useEventEmitter } from '@ace/react-utilities';
import SMARTGoalsOverlay from 'Components/SMARTGoal/SMARTGoalOverlay';
import { DashboardVariantID } from 'Components/Dashboard/DashboardHelper';
import styles from 'assets/css/SMARTGoal.module.css';
import { useQueryClient } from 'react-query';
import { queryProps, SMART_GOAL_DATA_EVENTS } from 'Components/SMARTGoal/SMARTGoalHelper';
import classNames from 'classnames';

const SMARTGoalOverlayClassContext = React.createContext(null);
const SMARTGoalOverlayContext = React.createContext(null);

//TODO: expose showOverlay and add class to DashboardContent div to kill scrolling
export function SMARTGoalOverlayContextProvider(props) {
    const { showOverlay, toggleOverlay, overlayRef } = useOverlayToggle();
    const queryClient = useQueryClient();
    const applyEmitterHandler = useEventEmitter(queryProps.updateUserGoal.componentName);

    const handleToggleClick = useCallback(() => {
        toggleOverlay();
    }, [toggleOverlay]);

    const goalsContextValue = useMemo(
        () => ({ toggleSMARTGoalsOverlay: handleToggleClick }),
        [handleToggleClick]
    );

    useEffect(() => {
        if (!showOverlay) {
            applyEmitterHandler((emitter) => {
                emitter.emit(SMART_GOAL_DATA_EVENTS.ON_OVERLAY_HIDDEN);
            });
        }
    }, [showOverlay, applyEmitterHandler]);

    useEffect(() => {
        queryClient.prefetchQuery(queryProps.appSteps.queryKey, () => defaultQueryFn(queryProps.appSteps.retrievalUrl));
        queryClient.prefetchQuery(queryProps.userGoals.queryKey, () => defaultQueryFn(queryProps.userGoals.retrievalUrl));
    }, [queryClient]);

    //render
    return (
        <SMARTGoalOverlayClassContext.Provider value={showOverlay}>
            <SMARTGoalOverlayContext.Provider value={goalsContextValue}>
                <div className={styles["sg-overlay__outer"]}>
                    {showOverlay && <SMARTGoalsOverlay ref={overlayRef} dashboardVariantID={DashboardVariantID} />}
                </div> 
                {props.children}
            </SMARTGoalOverlayContext.Provider>
        </SMARTGoalOverlayClassContext.Provider>
    );
}

const defaultContext = memo(() => ({
    toggleSMARTGoalsOverlay: () => { consoleLogDev("clicked disabled SMARTGoalsOverlay toggle().") },
    SMARTGoalsEnabled: false
}));

export default function useSMARTGoalOverlay() {
    const context = useContext(SMARTGoalOverlayContext);

    if (typeof context === 'undefined' || !context) {
        return defaultContext;
    }

    return ({
        toggleSMARTGoalsOverlay: context.toggleSMARTGoalsOverlay,
        SMARTGoalsEnabled: true
    });
}

export function useSMARTGoalDisplayOverlay() {
    const showOverlay = useContext(SMARTGoalOverlayClassContext);

    return classNames({
        'overflow-y-hidden': showOverlay
    });
}